import React, { useState } from "react";
import PropTypes from "prop-types";
import { Accordion as BootstrapAccordion, Card } from "react-bootstrap";
import styles from "./Accordion.scss";

const propTypes = {
  /**
   * Tabs of the Accordion
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  /**
   * Accordion style
   */
  appearance: PropTypes.oneOf(["block", "clean"]),
  /**
   * If true, display open/close arrow in the Accordion tab header
   */
  showArrow: PropTypes.bool,
  /**
   * If true, accordion will be collapsed first time
   */
  defaultCollapsed: PropTypes.bool,
};

const defaultProps = {
  appearance: "block",
  showArrow: false,
  defaultCollapsed: false,
};

/**
 * Accordion component based on https://react-bootstrap-v4.netlify.app/components/accordion/.
 */
function Accordion(props) {
  const { tabs, appearance, showArrow, defaultCollapsed } = props;

  // Since the bootstrap Accordion opens does not allow multiple open tabs
  // the workaround is to use multiple accordions to obtain the desired design
  return (
    <div>
      {tabs.map(({ id, label, content }) => (
        <AccordionTab
          key={id}
          id={id}
          label={label}
          content={content}
          appearance={appearance}
          showArrow={showArrow}
          defaultCollapsed={defaultCollapsed}
        />
      ))}
    </div>
  );
}

// disabling proptypes on the sub components below
// since they are already documented on the main component
/* eslint-disable react/prop-types */
function AccordionTab(props) {
  const { id, label, content, appearance, showArrow, defaultCollapsed } = props;

  const [selected, setSelected] = useState(defaultCollapsed ? null : id);

  return (
    <BootstrapAccordion
      defaultActiveKey={defaultCollapsed ? null : id}
      className={`${styles.base} ${styles[appearance]}`}
      onSelect={setSelected}
    >
      <Card>
        <BootstrapAccordion.Toggle
          as={Card.Header}
          eventKey={id}
          className="d-flex justify-content-between"
        >
          {label}
          <div
            className={`d-flex justify-content-end align-items-center ${styles.controls}`}
          >
            {showArrow && <Arrow open={!!selected} />}
          </div>
        </BootstrapAccordion.Toggle>
        <BootstrapAccordion.Collapse eventKey={id}>
          <Card.Body>{content}</Card.Body>
        </BootstrapAccordion.Collapse>
      </Card>
    </BootstrapAccordion>
  );
}

function Arrow(props) {
  const { open } = props;
  return (
    <i
      className={`${styles.arrow} fas fa-chevron-${open ? "down" : "right"}`}
    />
  );
}

Accordion.propTypes = propTypes;
Accordion.defaultProps = defaultProps;

export default Accordion;
